import { FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppLogo from "@/assets/images/favicon.png";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { TokenStatus } from "@/enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { renderErrorMessages } from "@/utils";

import {
	canRequestNewLink,
	getTokenStatusError,
	LinkProblem,
} from "../utils/set-password-response";

import { useResendInvitationTokenMutation } from "../../../redux/auth.api";

interface InvitationLinkNoticeProps {
	status: LinkProblem;
	token: string;
	/**
	 * Which token flow the notice speaks for — picks the error wording and the resend copy.
	 * Defaults to the original invitation flavor.
	 */
	flow?: "invitation" | "reset";
}

/**
 * Shown in place of the SetPassword / ChangePassword form when the link is not usable (missing
 * token / invalid / expired / already used), so the user learns why up front instead of after
 * submitting a password. For an expired link it offers a one-click resend path (backed by
 * `POST /token/resend`, which re-sends according to the token's own purpose — so it works for both
 * the invitation and the forgotten-password flow). For a missing or unknown token there is nothing
 * to resend, so it leads to the forgotten-password form instead (PREC-4022).
 */
export const InvitationLinkNotice: FunctionComponent<InvitationLinkNoticeProps> = ({
	status,
	token,
	flow = "invitation",
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [resend, { isLoading, isSuccess }] = useResendInvitationTokenMutation();

	const errorType = getTokenStatusError(status, flow);
	const canResend = status === TokenStatus.EXPIRED && Boolean(token);
	const offersNewLink = canRequestNewLink(status);

	const resendTitle =
		flow === "reset" ? t("setPassword.resendResetLink") : t("setPassword.resendInvitation");
	const resentMessage =
		flow === "reset" ? t("setPassword.resetLinkResent") : t("setPassword.invitationResent");

	return (
		<div className="w-full h-full flex flex-row items-center justify-center">
			<div className="flex flex-col bg-white p-6 rounded-[20px] w-[428px] max-w-[100%] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center">
				<img alt="logo" className="w-[48px] h-[48px] mb-4" src={AppLogo} />

				{errorType ? <div className="w-full mb-4">{renderErrorMessages([errorType])}</div> : null}

				{isSuccess ? (
					<div className="text-center text-ssm font-normal mb-4">{resentMessage}</div>
				) : canResend ? (
					<div className="w-full mb-3">
						<Button
							isLoading={isLoading}
							size={ButtonSize.ML}
							title={resendTitle}
							onClick={() => resend(token)}
						/>
					</div>
				) : offersNewLink ? (
					<div className="w-full mb-3">
						<Button
							size={ButtonSize.ML}
							title={t("setPassword.requestNewLink")}
							onClick={() => navigate(ROUTE_CONFIG.FORGOT_PASSWORD)}
						/>
					</div>
				) : null}

				<button
					className="text-brand-light text-ssm font-normal cursor-pointer hover:underline"
					type="button"
					onClick={() => navigate(ROUTE_CONFIG.LOGIN)}
				>
					{t("setPassword.backToLogin")}
				</button>
			</div>
		</div>
	);
};
